import { ApiError } from "@/lib/apiClient";
import {
  MarketingCampaign,
  CreateMarketingCampaignDto,
  UpdateMarketingCampaignDto,
  FetchMarketingCampaignsOptions,
  MarketingCampaignFilters,
} from "@/types/models/marketing";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface MarketingCampaignsState {
  items: MarketingCampaign[];
  loaded: boolean;
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  currentCampaign: MarketingCampaign | null;
  campaignLoading: boolean;
  campaignError: ApiError | null;

  searchResults: MarketingCampaign[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  formState: {
    loading: boolean;
    error: ApiError | null;
    success: boolean;
  };

  filters: MarketingCampaignFilters;
  fetchOptions: FetchMarketingCampaignsOptions;
}

const initialState: MarketingCampaignsState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  currentCampaign: null,
  campaignLoading: false,
  campaignError: null,

  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  formState: {
    loading: false,
    error: null,
    success: false,
  },

  filters: {},
  fetchOptions: {
    page: 1,
    page_size: 10,
    sort_by: "created_at",
    sort_order: "desc",
  },
};

const marketingCampaignSlice = createSlice({
  name: "marketingCampaigns",
  initialState,
  reducers: {
    fetchMarketingCampaignsRequest: (
      state,
      action: PayloadAction<FetchMarketingCampaignsOptions>,
    ) => {
      state.loading = true;
      state.error = null;
      state.fetchOptions = { ...state.fetchOptions, ...action.payload };
    },
    fetchMarketingCampaignsSuccess: (
      state,
      action: PayloadAction<{ data: MarketingCampaign[]; pagination: any }>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.items = action.payload.data;
      state.pagination = action.payload.pagination;
    },
    fetchMarketingCampaignsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },

    searchMarketingCampaignsRequest: (
      state,
      _action: PayloadAction<{
        query: string;
        options?: FetchMarketingCampaignsOptions;
      }>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchMarketingCampaignsSuccess: (
      state,
      action: PayloadAction<{ data: MarketingCampaign[]; pagination: any }>,
    ) => {
      state.searchLoading = false;
      state.searchResults = action.payload.data;
      state.searchPagination = action.payload.pagination;
    },
    searchMarketingCampaignsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.searchLoading = false;
      state.searchError = action.payload;
    },

    fetchMarketingCampaignDetailsRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.campaignLoading = true;
      state.campaignError = null;
    },
    fetchMarketingCampaignDetailsSuccess: (
      state,
      action: PayloadAction<MarketingCampaign>,
    ) => {
      state.campaignLoading = false;
      state.currentCampaign = action.payload;
    },
    fetchMarketingCampaignDetailsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.campaignLoading = false;
      state.campaignError = action.payload;
    },

    createMarketingCampaignRequest: (
      state,
      _action: PayloadAction<CreateMarketingCampaignDto>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createMarketingCampaignSuccess: (
      state,
      action: PayloadAction<MarketingCampaign>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = [action.payload, ...state.items];
    },
    createMarketingCampaignFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    updateMarketingCampaignRequest: (
      state,
      _action: PayloadAction<{ id: string; data: UpdateMarketingCampaignDto }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateMarketingCampaignSuccess: (
      state,
      action: PayloadAction<MarketingCampaign>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.currentCampaign = action.payload;

      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    updateMarketingCampaignFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    deleteMarketingCampaignRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    deleteMarketingCampaignSuccess: (
      state,
      action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload.id);
    },
    deleteMarketingCampaignFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    sendMarketingCampaignRequest: (
      state,
      _action: PayloadAction<{ id: string }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    sendMarketingCampaignSuccess: (
      state,
      action: PayloadAction<MarketingCampaign>,
    ) => {
      state.formState.loading = false;
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    sendMarketingCampaignFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    setFilters: (state, action: PayloadAction<MarketingCampaignFilters>) => {
      state.filters = action.payload;
    },
    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
      };
    },
    clearCurrentCampaign: (state) => {
      state.currentCampaign = null;
      state.campaignError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchMarketingCampaignsRequest,
  fetchMarketingCampaignsSuccess,
  fetchMarketingCampaignsFailure,
  searchMarketingCampaignsRequest,
  searchMarketingCampaignsSuccess,
  searchMarketingCampaignsFailure,
  fetchMarketingCampaignDetailsRequest,
  fetchMarketingCampaignDetailsSuccess,
  fetchMarketingCampaignDetailsFailure,
  createMarketingCampaignRequest,
  createMarketingCampaignSuccess,
  createMarketingCampaignFailure,
  updateMarketingCampaignRequest,
  updateMarketingCampaignSuccess,
  updateMarketingCampaignFailure,
  deleteMarketingCampaignRequest,
  deleteMarketingCampaignSuccess,
  deleteMarketingCampaignFailure,
  sendMarketingCampaignRequest,
  sendMarketingCampaignSuccess,
  sendMarketingCampaignFailure,
  setFilters,
  resetFormState,
  clearCurrentCampaign,
} = marketingCampaignSlice.actions;

export default marketingCampaignSlice.reducer;
